import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { ActionFeedbackResultsSchema, type FeedbackAction } from '@taqon/contracts';
import { errorMessage, getData } from '../../shared/api/client';
import { EmptyState, ErrorState, LoadingState, Section } from '../../shared/ui/page';
import { FACE_LABEL, Mix, share, totalOf } from './faces';

const RESULTS = '/api/v1/happiness/action-feedback/results';
const ACTION_NAME: Record<FeedbackAction, string> = {
  leave_request: 'Submitting a leave request',
  employee_request: 'Submitting an employee request',
  purchase_request: 'Submitting a purchase request',
  expense_claim: 'Submitting an expense claim',
};

/**
 * How submitting a request went (FRD-CORE-066; CLR-61): for a chosen period, the ratings of each
 * action by face, and the comments. Anonymous: nothing here names or identifies a person
 * (FRD-CORE-065). Declined offers are not counted.
 */
export function ActionFeedbackResults() {
  // The period the API was asked for; empty means its default, the last 30 days.
  const [period, setPeriod] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const query = new URLSearchParams();
  if (period.from !== '') query.set('from', period.from);
  if (period.to !== '') query.set('to', period.to);
  const results = useQuery({
    queryKey: ['happiness', 'action-feedback', 'results', period.from, period.to],
    queryFn: () =>
      getData(
        query.size === 0 ? RESULTS : `${RESULTS}?${query.toString()}`,
        ActionFeedbackResultsSchema,
      ),
  });
  const shownFrom = from ?? results.data?.from ?? period.from;
  const shownTo = to ?? results.data?.to ?? period.to;

  return (
    <>
      <Section title="Feedback on actions">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ p: 2.5, alignItems: { sm: 'flex-start' } }}
          component="form"
          aria-label="Feedback period"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setPeriod({ from: shownFrom, to: shownTo });
          }}
        >
          <TextField
            type="date"
            label="From"
            value={shownFrom}
            onChange={(event) => {
              setFrom(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            label="To"
            value={shownTo}
            onChange={(event) => {
              setTo(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button type="submit" variant="outlined" sx={{ mt: { sm: 1 } }}>
            Show
          </Button>
        </Stack>
        {results.isPending && <LoadingState label="Loading the feedback" />}
        {results.isError && <ErrorState message={errorMessage(results.error)} />}
        {results.data !== undefined && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Feedback by action">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell align="right">Happy</TableCell>
                  <TableCell align="right">Neutral</TableCell>
                  <TableCell align="right">Unhappy</TableCell>
                  <TableCell align="right">Ratings</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Mix</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.data.actions.map((row) => {
                  const total = totalOf(row);
                  return (
                    <TableRow key={row.action}>
                      <TableCell>{ACTION_NAME[row.action]}</TableCell>
                      <TableCell align="right">
                        {row.happy} ({share(row.happy, total)})
                      </TableCell>
                      <TableCell align="right">
                        {row.neutral} ({share(row.neutral, total)})
                      </TableCell>
                      <TableCell align="right">
                        {row.unhappy} ({share(row.unhappy, total)})
                      </TableCell>
                      <TableCell align="right">{total}</TableCell>
                      <TableCell>
                        <Mix counts={row} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Section>
      {results.data !== undefined && (
        <Section title="Feedback comments">
          {results.data.comments.length === 0 ? (
            <EmptyState title="No feedback comments in this period" />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Feedback comments">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Comment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.data.comments.map((comment, index) => (
                    // A comment carries no identifier, by design: its place in the list is its key.
                    <TableRow key={index}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{comment.answeredOn}</TableCell>
                      <TableCell>{ACTION_NAME[comment.action]}</TableCell>
                      <TableCell>{FACE_LABEL[comment.rating]}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {comment.comment}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {results.data.commentsTruncated && (
            <Typography variant="caption" sx={{ display: 'block', p: 2 }}>
              Only the most recent comments are shown. Choose a shorter period to see the rest.
            </Typography>
          )}
        </Section>
      )}
    </>
  );
}
